/** 프레젠 기기 로컬 TTS (브라우저 내장 window.speechSynthesis, 무료). 음성 데이터는 네트워크로 보내지 않는다. */
import type { RemoteTtsSettings, RemoteTtsVoiceInfo } from '../types/presentationRemote';

export const DEFAULT_TTS_SETTINGS: RemoteTtsSettings = { voiceURI: null, rate: 0.95, pitch: 1, volume: 1 };
const TTS_LS_PREFIX = 'cp-pres-remote-tts-v1:';
const VOICE_CAP = 50;

let activeSettings: RemoteTtsSettings = { ...DEFAULT_TTS_SETTINGS };

const clamp = (v: unknown, lo: number, hi: number, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : d;
};

export function sanitizeTtsSettings(v: Partial<RemoteTtsSettings> | null | undefined): RemoteTtsSettings {
  return {
    voiceURI: typeof v?.voiceURI === 'string' && v.voiceURI ? v.voiceURI.slice(0, 300) : null,
    rate: clamp(v?.rate, 0.5, 1.5, DEFAULT_TTS_SETTINGS.rate),
    pitch: clamp(v?.pitch, 0.5, 1.5, DEFAULT_TTS_SETTINGS.pitch),
    volume: clamp(v?.volume, 0, 1, DEFAULT_TTS_SETTINGS.volume),
  };
}

export function getActiveTtsSettings(): RemoteTtsSettings {
  return activeSettings;
}

/** 계정+기기별 저장값 로드 후 활성화 */
export function loadTtsSettings(userId: string, deviceId: string): RemoteTtsSettings {
  try {
    const raw = window.localStorage.getItem(`${TTS_LS_PREFIX}${userId}:${deviceId}`);
    activeSettings = sanitizeTtsSettings(raw ? JSON.parse(raw) : null);
  } catch {
    activeSettings = { ...DEFAULT_TTS_SETTINGS };
  }
  return activeSettings;
}

export function saveTtsSettings(userId: string, deviceId: string, next: Partial<RemoteTtsSettings>): RemoteTtsSettings {
  activeSettings = sanitizeTtsSettings(next);
  try {
    window.localStorage.setItem(`${TTS_LS_PREFIX}${userId}:${deviceId}`, JSON.stringify(activeSettings));
  } catch {
    /* ignore */
  }
  return activeSettings;
}

/** getVoices()는 비동기로 채워지는 브라우저가 있어 voiceschanged를 최대 1.5초 기다림 */
export function getVoicesAsync(): Promise<SpeechSynthesisVoice[]> {
  if (!isSpeechAvailable()) return Promise.resolve([]);
  const synth = window.speechSynthesis;
  const now = synth.getVoices();
  if (now.length) return Promise.resolve(now);
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      synth.removeEventListener('voiceschanged', finish);
      resolve(synth.getVoices());
    };
    synth.addEventListener('voiceschanged', finish);
    window.setTimeout(finish, 1500);
  });
}

const isKo = (lang: string) => String(lang ?? '').toLowerCase().startsWith('ko');

/** 한국어 우선, 최대 50개 */
export async function listVoicesCompact(includeAll: boolean): Promise<{ voices: RemoteTtsVoiceInfo[]; total: number; koCount: number }> {
  const all = await getVoicesAsync();
  const ko = all.filter((v) => isKo(v.lang));
  const rest = includeAll ? all.filter((v) => !isKo(v.lang)).sort((a, b) => a.lang.localeCompare(b.lang)) : [];
  const voices = [...ko, ...rest].slice(0, VOICE_CAP).map((v) => ({
    voiceURI: v.voiceURI,
    name: v.name.slice(0, 80),
    lang: v.lang,
    localService: !!v.localService,
    default: !!v.default,
  }));
  return { voices, total: all.length, koCount: ko.length };
}

export type SpeakResult = 'ok' | 'unavailable' | 'blocked';

const START_TIMEOUT_MS = 2500;

export function isSpeechAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
}

function pickKoreanVoice(preferredURI?: string | null): SpeechSynthesisVoice | null {
  try {
    const voices = window.speechSynthesis.getVoices();
    const chosen = preferredURI ? voices.find((v) => v.voiceURI === preferredURI) : undefined;
    if (chosen) return chosen;
    /* 저장된 음성이 없으면 기본 한국어 */
    return (
      voices.find((v) => v.lang === 'ko-KR' && v.localService) ??
      voices.find((v) => v.lang === 'ko-KR') ??
      voices.find((v) => v.lang?.toLowerCase().startsWith('ko')) ??
      null
    );
  } catch {
    return null;
  }
}

export function cancelSpeech(): void {
  try {
    if (isSpeechAvailable()) window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

/**
 * 텍스트 읽기. 시작되면 'ok', 정책상 막히면 'blocked'(사용자 제스처 필요), 미지원이면 'unavailable'.
 * onEnd는 읽기가 끝나거나 취소·오류일 때 1회 호출.
 */
export function speakText(
  text: string,
  opts?: { settings?: RemoteTtsSettings; onEnd?: () => void }
): Promise<SpeakResult> {
  if (!isSpeechAvailable()) return Promise.resolve('unavailable');
  const clean = String(text ?? '').trim().slice(0, 500);
  if (!clean) return Promise.resolve('ok');
  return new Promise((resolve) => {
    let settled = false;
    let ended = false;
    const end = () => {
      if (ended) return;
      ended = true;
      opts?.onEnd?.();
    };
    const settle = (r: SpeakResult) => {
      if (settled) return;
      settled = true;
      resolve(r);
    };
    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(clean);
      const st = opts?.settings ?? activeSettings;
      const voice = pickKoreanVoice(st.voiceURI);
      u.lang = voice?.lang || 'ko-KR';
      if (voice) u.voice = voice;
      u.rate = st.rate;
      u.pitch = st.pitch;
      u.volume = st.volume;
      u.onstart = () => settle('ok');
      u.onend = () => {
        settle('ok');
        end();
      };
      u.onerror = (ev: SpeechSynthesisErrorEvent) => {
        const err = ev?.error;
        if (err === 'interrupted' || err === 'canceled') settle('ok');
        else settle(err === 'not-allowed' ? 'blocked' : 'unavailable');
        end();
      };
      synth.speak(u);
      window.setTimeout(() => {
        if (settled) return;
        /* 시작 이벤트가 오지 않으면 자동재생 정책 차단으로 간주 */
        if (synth.speaking && !synth.paused) settle('ok');
        else {
          synth.cancel();
          settle('blocked');
          end();
        }
      }, START_TIMEOUT_MS);
    } catch {
      settle('unavailable');
      end();
    }
  });
}
